<!-- Loading state -->
<div class="loading-state" *ngIf="!resultReady">
  <qai-loading size="medium" />
  <span class="loading-label">{{
    "results.PROCESSING_RESULTS" | transloco
  }}</span>
</div>

<ng-container *ngIf="resultReady && translationLoaded">
  <!-- Hero -->
  <div class="hero">
    <div class="hero-text">
      <h2 class="hero-title">
        {{ "results.QUIZ_RESULTS_SUMMARY" | transloco }}
      </h2>
      <p class="hero-subtitle">
        {{
          embeddedQuizSummaryTranslationKey
            | transloco
              : {
                  stepNames: embeddedQuizReviewStepNamesText,
                  stepWord: embeddedQuizStepWord,
                  stepVerb: embeddedQuizStepVerb,
                  areaWord: embeddedQuizAreaWord,
                }
        }}
      </p>
    </div>

    <div class="hero-score">
      <!-- Out of compliance replaces the score -->
      @if (isUserCompliant === false) {
        <div class="score-badge warning">
          <div class="score-value">{{ "results.OUT_OF_COMPLIANCE" | transloco }}</div>
        </div>
      } @else {
        <div
          class="score-badge"
          [ngClass]="{
            success: hasPassed,
          }"
        >
          <div class="score-value">
            {{
              callScoreCalcType === calcTypeRatio5pt0min
                ? (practiceScore | number: "1.1-2")
                : callScoreCalcType === "RATIO_100PT_0MIN"
                  ? localeService.formatPercentage(practiceScore, localeService.getDecimalPlaces(practiceScore, 2))
                  : (practiceScore | number: "1.0-2")
            }}
          </div>
          <div class="score-label">
            {{ totalPositivePoints }}/{{ totalPoints }}
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Out of compliance: guidelines the learner missed -->
  @if (isUserCompliant === false) {
    <div class="feedback">
      <div class="content">
        <div class="questions">
          <app-compliance-missed-guidelines class="question-result" [complianceScoringResult]="complianceScoringResult" [complianceOnly]="complianceOnly" />
        </div>
      </div>
    </div>
  }

  <!-- Feedback Content -->
  @if (showFeedback) {
    <div class="feedback">
      <div class="content">
        <div class="questions">
          <div
            class="question-result"
            *ngFor="let question of guidedResults?.questions; let i = index"
          >
            <div class="question">
              <div class="question-name-thumbnail">
                <div class="thumbnail-wrapper"
                  *ngIf="question.visualAidIndex && question.visualAidSrc">
                  <img [src]="question.visualAidSrc" [alt]="'results.THUMBNAIL' | transloco"
                    class="thumbnail" />
                  <img (click)="openImageModal(question.visualAidSrc)" class="expand-icon"
                    src="assets/icons/expand.svg">
                </div>
                <span>
                  {{ question.name }}
                </span>
              </div>
              <div class="point-collapser">
                <span class="question-score">
                  {{question.practiceCallGuided.positiveContext.length}}/{{question.practiceCallGuided.positiveContext.length
                  + question.practiceCallGuided.negativeContext.length}}
                </span>
                <button type="button" (click)="toggle(i)" class="question-toggle">
                  <qai-icon size="24px" [kind]="isExpandedd(i) ? 'chevron-down' : 'chevron-left'" />
                </button>
              </div>
            </div>

            <div class="talking-points" *ngIf="isExpandedd(i)">
              <!-- Covered -->
              <div class="talking-point">
                <div class="talking-point-title">{{'results.TALKING_POINTS_COVERED' | transloco}}</div>
                <div class="points">
                  <div class="points-list" *ngIf="question.practiceCallGuided.positiveContext.length > 0">
                    <div class="point" *ngFor="
                          let point of question.practiceCallGuided.positiveContext
                        ">
                      <img src="/assets/icons/check-2.svg" [alt]="'results.CHECKMARK_ICON_ALT' | transloco" />
                      {{ point }}
                    </div>
                  </div>
                </div>
              </div>
              <!-- Missed -->
              <div class="talking-point">
                <div class="talking-point-title">{{'results.TALKING_POINTS_MISSED' | transloco}}</div>
                <div class="points">
                  <ng-container *ngIf="
                        question.practiceCallGuided.negativeContext.length > 0;
                        else emptyNegativeContext
                      ">
                    <!-- Points list (only when facts are allowed to be shown) -->
                    <div class="points-list" *ngIf="showMissedFacts; else missedFactsHidden">
                      <div class="point" *ngFor="
                            let point of question.practiceCallGuided.negativeContext
                          ">
                        <img src="/assets/icons/x-error.svg" [alt]="'results.X_ICON_ALT' | transloco" />
                        {{ point }}
                      </div>
                    </div>

                    <ng-template #missedFactsHidden>
                      <!-- Points hidden and 0 covered: bold summary, no individual points -->
                      <div class="points-list" *ngIf="
                            question.practiceCallGuided.positiveContext.length === 0;
                            else someCoveredFactsHidden
                          ">
                        <div class="point">
                          <img src="/assets/icons/x-error.svg" [alt]="'results.X_ICON_ALT' | transloco" />
                          <div>
                            <div class="missed-all-title">
                              {{
                                question.practiceCallGuided.negativeContext.length === 1
                                  ? ('results.MISSED_ALL_ONE_REQUIRED_TALKING_POINT_TITLE' | transloco)
                                  : ('results.MISSED_ALL_X_REQUIRED_TALKING_POINTS_TITLE' | transloco: { count: question.practiceCallGuided.negativeContext.length })
                              }}
                            </div>
                            <div>{{'results.REVIEW_MATERIAL_NEXT_TIME' | transloco}}</div>
                          </div>
                        </div>
                      </div>

                      <!-- Points hidden but some points covered: plain one-line summary -->
                      <ng-template #someCoveredFactsHidden>
                        <div class="points-list">
                          <div class="point">
                            <img src="/assets/icons/x-error.svg" [alt]="'results.X_ICON_ALT' | transloco" />
                            {{
                              question.practiceCallGuided.negativeContext.length === 1
                                ? ('results.MISSED_ONE_REQUIRED_TALKING_POINT' | transloco)
                                : ('results.MISSED_X_REQUIRED_TALKING_POINTS' | transloco: { count: question.practiceCallGuided.negativeContext.length })
                            }}
                          </div>
                        </div>
                      </ng-template>
                    </ng-template>

                    <!-- Resource reference (only if points are missed and shown) -->
                    <qai-alert class="resource" kind="hint" [title]="'results.LEARN_WHAT_YOU_MISSED' | transloco" *ngIf="
                          showMissedFacts &&
                          question.reference
                        ">
                      <div [innerHTML]="question.reference"></div>
                    </qai-alert>
                  </ng-container>

                  <!-- Message, covered all points -->
                  <ng-template #emptyNegativeContext>
                    <div class="message">
                      <strong>{{'results.NICE_JOB' | transloco}}</strong> {{'results.ALL_POINTS_COVERED' | transloco}}
                    </div>
                  </ng-template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  }
</ng-container>
